/**
 * Flotte configurable components (Phase 7 — fiches 22756 / 22809 / 22811 /
 * 22768 / 22776 / 22778):
 *
 * | id | component | data props | stable keys |
 * |---|---|---|---|
 * | `extranet.fleetListCard` | `FleetListCard` (DataTableCard, detached server-side search `query`) | `vehicles` | columns `immatriculation`, `marqueModele`, `dateEntree`, `dateSortie`, `statut`; search |
 * | `extranet.vehicleDetailsCard` | `EntityInfoCard` usage | `values` | items `immatriculation`, `marque`, `modele`, `miseEnCirculation`, `conducteur`, `dateEntree`, `statut` |
 * | `extranet.vehicleContractCard` | `VehicleContractCard` (EntityInfoCard + action) | `values`, `contractPath` | items `numero`, `produit`, `assureur`, `assure`, `dateEffet`, `dateEcheance`, `primeAnnuelle`, `fractionnement`; action `viewContract` |
 * | `extranet.vehicleGuaranteesCard` | `GuaranteesTableCard` usage | `rows` | columns `libelle`, `plafond`, `franchise` |
 * | `extranet.vehicleClaimsCard` | `ClaimsTableCard` usage | `rows` | columns `reference`, `circonstances`, `dateSurvenance`, `coutAssureur` |
 * | `extranet.vehicleDocumentsCard` | `DocumentsByCategoryCard` usage | `documents` | items `nom`, `date` |
 *
 * Every data prop set also takes `loading` / `error`. Pages import the
 * `…Configurable` wrappers below (aliased to the plain name) and wrap their
 * body in `RuleContextProvider` (base context on `/flotte`,
 * `vehicleContextSchema` on the vehicle detail).
 */

import { withComponentRules } from '@components/shared/configurable';

import { extranetBaseContextSchema } from '../../contextVariables';
import { EXTRANET_MODULE, registerOnce } from '../registerHelpers';
import { bindCardProps } from '../shared/bindCardProps';
import { ClaimsTableCard } from '../shared/ClaimsTableCard';
import { DocumentsByCategoryCard } from '../shared/DocumentsByCategoryCard';
import { EntityInfoCard } from '../shared/EntityInfoCard';
import { GuaranteesTableCard } from '../shared/GuaranteesTableCard';
import {
  claimsTableCardDataSchema,
  documentsCardDataSchema,
  entityInfoCardDataSchema,
  guaranteesTableCardDataSchema,
  type ClaimsTableCardDataProps,
  type DocumentsCardDataProps,
  type EntityInfoCardDataProps,
  type GuaranteesTableCardDataProps,
} from '../shared/schemas';
import { FLEET_COMPONENT_IDS } from './fleetComponentIds';
import { FleetListCard } from './FleetListCard';
import {
  FLEET_LIST_PREVIEW_PROPS,
  FLEET_PROP_LABELS,
  VEHICLE_CLAIMS_PREVIEW_PROPS,
  VEHICLE_CONTRACT_PREVIEW_PROPS,
  VEHICLE_DETAILS_PREVIEW_PROPS,
  VEHICLE_DOCUMENTS_PREVIEW_PROPS,
  VEHICLE_GUARANTEES_PREVIEW_PROPS,
  fleetListCardDataSchema,
  vehicleContractCardDataSchema,
  type FleetListCardDataProps,
  type VehicleContractCardDataProps,
} from './schemas';
import { VehicleContractCard } from './VehicleContractCard';
import { vehicleContextSchema } from './vehicleContext';
import { VEHICLE_DETAILS_FIELDS } from './vehicleInfoConfig';

export { FLEET_COMPONENT_IDS } from './fleetComponentIds';

// ---------------------------------------------------------------------------
// Usages of the shared cards (config bound, data props left open)
// ---------------------------------------------------------------------------

const VehicleDetailsCard = bindCardProps(EntityInfoCard, {
  componentId: FLEET_COMPONENT_IDS.vehicleDetails,
  fields: VEHICLE_DETAILS_FIELDS,
});

const VehicleGuaranteesCard = bindCardProps(GuaranteesTableCard, {
  componentId: FLEET_COMPONENT_IDS.vehicleGuarantees,
});

const VehicleClaimsCard = bindCardProps(ClaimsTableCard, {
  componentId: FLEET_COMPONENT_IDS.vehicleClaims,
});

const VehicleDocumentsCard = bindCardProps(DocumentsByCategoryCard, {
  componentId: FLEET_COMPONENT_IDS.vehicleDocuments,
});

const STATE_PROP_LABELS = {
  loading: FLEET_PROP_LABELS.loading,
  error: FLEET_PROP_LABELS.error,
};

/** Register the Flotte components. Idempotent (HMR / test re-imports). */
export function registerFleetComponents(): void {
  registerOnce<FleetListCardDataProps>({
    id: FLEET_COMPONENT_IDS.fleetList,
    displayName: 'Liste des véhicules',
    module: EXTRANET_MODULE,
    component: FleetListCard,
    propsSchema: fleetListCardDataSchema,
    defaultPropLabels: { vehicles: FLEET_PROP_LABELS.vehicles, ...STATE_PROP_LABELS },
    previewProps: FLEET_LIST_PREVIEW_PROPS,
    contextVariables: extranetBaseContextSchema,
  });

  registerOnce<EntityInfoCardDataProps>({
    id: FLEET_COMPONENT_IDS.vehicleDetails,
    displayName: 'Détails du véhicule',
    module: EXTRANET_MODULE,
    component: VehicleDetailsCard,
    propsSchema: entityInfoCardDataSchema,
    defaultPropLabels: { values: FLEET_PROP_LABELS.values, ...STATE_PROP_LABELS },
    previewProps: VEHICLE_DETAILS_PREVIEW_PROPS,
    contextVariables: vehicleContextSchema,
  });

  registerOnce<VehicleContractCardDataProps>({
    id: FLEET_COMPONENT_IDS.vehicleContract,
    displayName: 'Contrat du véhicule',
    module: EXTRANET_MODULE,
    component: VehicleContractCard,
    propsSchema: vehicleContractCardDataSchema,
    defaultPropLabels: {
      values: FLEET_PROP_LABELS.values,
      contractPath: FLEET_PROP_LABELS.contractPath,
      ...STATE_PROP_LABELS,
    },
    previewProps: VEHICLE_CONTRACT_PREVIEW_PROPS,
    contextVariables: vehicleContextSchema,
  });

  registerOnce<GuaranteesTableCardDataProps>({
    id: FLEET_COMPONENT_IDS.vehicleGuarantees,
    displayName: 'Garanties du véhicule',
    module: EXTRANET_MODULE,
    component: VehicleGuaranteesCard,
    propsSchema: guaranteesTableCardDataSchema,
    defaultPropLabels: { rows: FLEET_PROP_LABELS.rows, ...STATE_PROP_LABELS },
    previewProps: VEHICLE_GUARANTEES_PREVIEW_PROPS,
    contextVariables: vehicleContextSchema,
  });

  registerOnce<ClaimsTableCardDataProps>({
    id: FLEET_COMPONENT_IDS.vehicleClaims,
    displayName: 'Sinistres du véhicule',
    module: EXTRANET_MODULE,
    component: VehicleClaimsCard,
    propsSchema: claimsTableCardDataSchema,
    defaultPropLabels: { rows: FLEET_PROP_LABELS.rows, ...STATE_PROP_LABELS },
    previewProps: VEHICLE_CLAIMS_PREVIEW_PROPS,
    contextVariables: vehicleContextSchema,
  });

  registerOnce<DocumentsCardDataProps>({
    id: FLEET_COMPONENT_IDS.vehicleDocuments,
    displayName: 'Documents du véhicule',
    module: EXTRANET_MODULE,
    component: VehicleDocumentsCard,
    propsSchema: documentsCardDataSchema,
    defaultPropLabels: { documents: FLEET_PROP_LABELS.documents, ...STATE_PROP_LABELS },
    previewProps: VEHICLE_DOCUMENTS_PREVIEW_PROPS,
    contextVariables: vehicleContextSchema,
  });
}

// ---------------------------------------------------------------------------
// Rule-aware wrappers imported by the pages
// ---------------------------------------------------------------------------

export const FleetListCardConfigurable = withComponentRules(
  FleetListCard,
  FLEET_COMPONENT_IDS.fleetList,
);

export const VehicleDetailsCardConfigurable = withComponentRules(
  VehicleDetailsCard,
  FLEET_COMPONENT_IDS.vehicleDetails,
);

export const VehicleContractCardConfigurable = withComponentRules(
  VehicleContractCard,
  FLEET_COMPONENT_IDS.vehicleContract,
);

export const VehicleGuaranteesCardConfigurable = withComponentRules(
  VehicleGuaranteesCard,
  FLEET_COMPONENT_IDS.vehicleGuarantees,
);

export const VehicleClaimsCardConfigurable = withComponentRules(
  VehicleClaimsCard,
  FLEET_COMPONENT_IDS.vehicleClaims,
);

export const VehicleDocumentsCardConfigurable = withComponentRules(
  VehicleDocumentsCard,
  FLEET_COMPONENT_IDS.vehicleDocuments,
);
